import React, { useCallback, useEffect, useState } from 'react'
import { Drawer, Select } from 'antd'
import { Button } from '@surecompanies/core_components'

import { VcnManagementCompanySelect } from './VcnManagementCompanySelect'
import type {
  PrimaryPaymentMethodFilter,
  VcnManagementBenefitFilters,
  VcnCardStatus,
  VcnProviderFilter,
} from './types'
import {
  buildFiltersFromSidebarDraft,
  DEFAULT_VCN_MANAGEMENT_FILTERS,
  FILTER_ALL_VALUE,
  fromFilterSelectValue,
  PAYMENT_METHOD_OPTIONS,
  PROVIDER_OPTIONS,
  STATUS_OPTIONS,
  toFilterSelectValue,
} from './vcnManagementFilterDefaults'
import { useVcnManagementCarriers } from './hooks/useVcnManagementCarriers'
import {
  FilterSection,
  FilterSectionTitle,
  FilterLabel,
  Footer,
  ResetButton,
} from './VcnManagementSidebar.styles'

const EMPTY_COMPANY_KEYS: string[] = []

export interface VcnManagementBenefitSidebarProps {
  open: boolean
  onClose: () => void
  appliedFilters: VcnManagementBenefitFilters
  onApply: (next: VcnManagementBenefitFilters) => void
}

function draftFromApplied(
  applied: VcnManagementBenefitFilters
): VcnManagementBenefitFilters {
  return {
    clientPublicKeys: applied.clientPublicKeys,
    carrierName: applied.carrierName,
    vcnProvider: applied.vcnProvider,
    vcnStatus: applied.vcnStatus,
    primaryPaymentMethod: applied.primaryPaymentMethod,
  }
}

export function VcnManagementBenefitSidebar({
  open,
  onClose,
  appliedFilters,
  onApply,
}: VcnManagementBenefitSidebarProps) {
  const [draft, setDraft] = useState<VcnManagementBenefitFilters>(
    DEFAULT_VCN_MANAGEMENT_FILTERS
  )

  const { data: carrierNames, isLoading: isLoadingCarriers } =
    useVcnManagementCarriers()

  const carrierOptions = React.useMemo(() => {
    const dynamic = (carrierNames ?? []).map((name) => ({
      value: name,
      label: name,
    }))
    return [{ value: FILTER_ALL_VALUE, label: 'All carriers' }, ...dynamic]
  }, [carrierNames])

  useEffect(() => {
    if (!open) return
    setDraft(draftFromApplied(appliedFilters))
  }, [open, appliedFilters])

  const handleApply = useCallback(() => {
    onApply(buildFiltersFromSidebarDraft(appliedFilters, draft))
    onClose()
  }, [appliedFilters, draft, onApply, onClose])

  const handleReset = useCallback(() => {
    setDraft({ ...DEFAULT_VCN_MANAGEMENT_FILTERS })
  }, [])

  return (
    <Drawer
      title={<span style={{ fontWeight: 600, fontSize: 16 }}>Filters</span>}
      placement="right"
      onClose={onClose}
      open={open}
      width={420}
      zIndex={1050}
      destroyOnClose={false}
      maskStyle={{ backgroundColor: 'rgba(0, 49, 70, 0.42)' }}
      drawerStyle={{
        boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.16)',
      }}
      headerStyle={{
        padding: '16px 24px',
        borderBottom: '1px solid #e8e8e8',
      }}
      bodyStyle={{
        padding: '16px 24px 24px',
      }}
      footerStyle={{
        borderTop: '1px solid #e8e8e8',
        padding: '16px 24px',
        background: '#fafafa',
      }}
      footer={
        <Footer>
          <Button type="primary" onClick={handleApply}>
            Apply filters
          </Button>
          <ResetButton onClick={handleReset}>Reset to defaults</ResetButton>
        </Footer>
      }
    >
      <FilterSection>
        <FilterSectionTitle>Company</FilterSectionTitle>
        <FilterLabel>Company name</FilterLabel>
        <VcnManagementCompanySelect
          immediateCommit
          style={{ width: '100%' }}
          value={draft.clientPublicKeys ?? EMPTY_COMPANY_KEYS}
          onCommit={(keys) =>
            setDraft((d) => ({
              ...d,
              clientPublicKeys: keys.length > 0 ? keys : undefined,
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Carrier</FilterSectionTitle>
        <FilterLabel>Carrier name</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          loading={isLoadingCarriers}
          options={carrierOptions}
          value={toFilterSelectValue(draft.carrierName)}
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              carrierName: fromFilterSelectValue<string>(value),
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>Payment method</FilterSectionTitle>
        <FilterLabel>Primary payment method</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          options={[...PAYMENT_METHOD_OPTIONS]}
          value={toFilterSelectValue(draft.primaryPaymentMethod)}
          onChange={(value) => {
            const next =
              fromFilterSelectValue<PrimaryPaymentMethodFilter>(value)
            setDraft((d) => ({
              ...d,
              primaryPaymentMethod: next,
              // ACH benefits have no VCN card, so the provider filter does not
              // apply — clear it (and the control is disabled below) to avoid a
              // stale provider staying applied under a disabled field.
              vcnProvider: next === 'ACH' ? undefined : d.vcnProvider,
            }))
          }}
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>VCN Provider</FilterSectionTitle>
        <FilterLabel>Provider</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          disabled={draft.primaryPaymentMethod === 'ACH'}
          options={[...PROVIDER_OPTIONS]}
          value={toFilterSelectValue(draft.vcnProvider)}
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              vcnProvider: fromFilterSelectValue<VcnProviderFilter>(value),
            }))
          }
        />
      </FilterSection>

      <FilterSection>
        <FilterSectionTitle>VCN status</FilterSectionTitle>
        <FilterLabel>Status</FilterLabel>
        <Select
          style={{ width: '100%' }}
          size="large"
          options={[...STATUS_OPTIONS]}
          value={toFilterSelectValue(draft.vcnStatus)}
          onChange={(value) =>
            setDraft((d) => ({
              ...d,
              vcnStatus: fromFilterSelectValue<VcnCardStatus>(value),
            }))
          }
        />
      </FilterSection>
    </Drawer>
  )
}
